import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";

import {
  Client,
  ClientMergePreview,
  getClients,
  mergeClients,
  previewClientMerge,
} from "../../services/clientService";

type ClientMergeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialKeepId?: number | null;
  onMerged?: () => void;
};

const friendlyApiError = (error: unknown, fallback: string): string => {
  const err = error as { data?: string; message?: string };
  if (typeof err?.data === "string" && err.data.trim() !== "") {
    return err.data;
  }
  if (typeof err?.message === "string" && err.message.trim() !== "") {
    return err.message;
  }
  return fallback;
};

const normalizeName = (name: string): string =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const normalizeDoc = (value: string | null | undefined): string =>
  (value ?? "").replace(/\D/g, "");

const ClientMergeModal = ({
  isOpen,
  onClose,
  initialKeepId = null,
  onMerged,
}: ClientMergeModalProps) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [loadingClients, setLoadingClients] = useState(false);
  const [keepId, setKeepId] = useState<string>("");
  const [absorbId, setAbsorbId] = useState<string>("");
  const [preview, setPreview] = useState<ClientMergePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [search, setSearch] = useState("");

  const reset = () => {
    setKeepId(initialKeepId ? String(initialKeepId) : "");
    setAbsorbId("");
    setPreview(null);
    setSearch("");
    setLoadingPreview(false);
    setConfirming(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset();
    setLoadingClients(true);
    void getClients({ per_page: 100, page: 1 })
      .then((response) => {
        setClients(response.data ?? []);
        setTotalClients(response.meta?.total ?? response.data?.length ?? 0);
      })
      .catch((error) => {
        toast.error(friendlyApiError(error, t("clients.merge.load_error")));
      })
      .finally(() => setLoadingClients(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialKeepId]);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return clients;
    }
    return clients.filter((client) => {
      const haystack = `${client.name} ${client.phone_number} ${client.email ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [clients, search]);

  const suggestedAbsorb = useMemo(() => {
    if (!keepId) {
      return [] as Array<{ client: Client; reason: "name" | "document" }>;
    }

    const keep = clients.find((client) => String(client.id) === keepId);
    if (!keep) {
      return [];
    }

    const keepName = normalizeName(keep.name);
    const keepDoc = normalizeDoc(keep.document_number);
    const results: Array<{ client: Client; reason: "name" | "document" }> = [];

    for (const client of clients) {
      if (String(client.id) === keepId) {
        continue;
      }

      const sameDoc =
        keepDoc.length >= 11 && normalizeDoc(client.document_number) === keepDoc;
      const sameName = keepName.length >= 3 && normalizeName(client.name) === keepName;

      if (sameDoc) {
        results.push({ client, reason: "document" });
      } else if (sameName) {
        results.push({ client, reason: "name" });
      }

      if (results.length >= 5) {
        break;
      }
    }

    return results;
  }, [clients, keepId]);

  const possiblePairs = useMemo(() => {
    if (keepId || clients.length < 2) {
      return [] as Array<{ keep: Client; absorb: Client; reason: "name" | "document" }>;
    }

    const byName = new Map<string, Client[]>();
    const byDoc = new Map<string, Client[]>();

    for (const client of clients) {
      const nameKey = normalizeName(client.name);
      if (nameKey.length >= 3) {
        const list = byName.get(nameKey) ?? [];
        list.push(client);
        byName.set(nameKey, list);
      }

      const docKey = normalizeDoc(client.document_number);
      if (docKey.length >= 11) {
        const list = byDoc.get(docKey) ?? [];
        list.push(client);
        byDoc.set(docKey, list);
      }
    }

    const pairs: Array<{ keep: Client; absorb: Client; reason: "name" | "document" }> = [];
    const seen = new Set<string>();

    const pushPair = (a: Client, b: Client, reason: "name" | "document") => {
      const key = [a.id, b.id].sort((x, y) => x - y).join(":");
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      pairs.push({ keep: a, absorb: b, reason });
    };

    Array.from(byDoc.values()).forEach((group) => {
      if (pairs.length >= 5 || group.length < 2) {
        return;
      }
      pushPair(group[0], group[1], "document");
    });

    Array.from(byName.values()).forEach((group) => {
      if (pairs.length >= 5 || group.length < 2) {
        return;
      }
      pushPair(group[0], group[1], "name");
    });

    return pairs;
  }, [clients, keepId]);

  const selectPair = (nextKeepId: number, nextAbsorbId: number) => {
    setKeepId(String(nextKeepId));
    setAbsorbId(String(nextAbsorbId));
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!keepId || !absorbId || keepId === absorbId) {
      toast.error(t("clients.merge.select_two"));
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await previewClientMerge(Number(keepId), Number(absorbId));
      setPreview(response.data);
    } catch (error) {
      setPreview(null);
      toast.error(friendlyApiError(error, t("clients.merge.preview_error")));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.can_merge || !keepId || !absorbId) {
      return;
    }

    setConfirming(true);
    try {
      await mergeClients(Number(keepId), Number(absorbId));
      toast.success(t("clients.merge.success"));
      onMerged?.();
      onClose();
    } catch (error) {
      toast.error(friendlyApiError(error, t("clients.merge.confirm_error")));
    } finally {
      setConfirming(false);
    }
  };

  const tooFewClients = !loadingClients && totalClients < 2;

  return (
    <Modal isOpen={isOpen} toggle={confirming ? undefined : onClose} centered size="lg">
      <ModalHeader toggle={confirming ? undefined : onClose}>
        {t("clients.merge.title")}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted">{t("clients.merge.instructions")}</p>

        {loadingClients ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : tooFewClients ? (
          <Alert color="info" className="mb-0">
            <p className="mb-0">{t("clients.merge.empty_state")}</p>
          </Alert>
        ) : (
          <>
            {possiblePairs.length > 0 ? (
              <Alert color="light" className="border mb-3">
                <strong className="d-block mb-2">{t("clients.merge.suggestions_title")}</strong>
                <ul className="mb-0 ps-3">
                  {possiblePairs.map(({ keep, absorb, reason }) => (
                    <li key={`${keep.id}-${absorb.id}`} className="mb-1">
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 align-baseline"
                        onClick={() => selectPair(keep.id, absorb.id)}
                      >
                        {keep.name} ↔ {absorb.name}
                      </Button>
                      <small className="text-muted ms-2">
                        {t(`clients.merge.suggestion_reason.${reason}`)}
                      </small>
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            <div className="mb-3">
              <Label htmlFor="merge-search">{t("clients.merge.search")}</Label>
              <Input
                id="merge-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("clients.search.placeholder")}
              />
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label htmlFor="merge-keep">{t("clients.merge.keep_label")}</Label>
                <Input
                  id="merge-keep"
                  type="select"
                  value={keepId}
                  onChange={(event) => {
                    setKeepId(event.target.value);
                    setAbsorbId("");
                    setPreview(null);
                  }}
                >
                  <option value="">{t("clients.merge.choose")}</option>
                  {filteredClients.map((client) => (
                    <option key={`keep-${client.id}`} value={client.id}>
                      {client.name} — {client.phone_number}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="col-md-6">
                <Label htmlFor="merge-absorb">{t("clients.merge.absorb_label")}</Label>
                <Input
                  id="merge-absorb"
                  type="select"
                  value={absorbId}
                  onChange={(event) => {
                    setAbsorbId(event.target.value);
                    setPreview(null);
                  }}
                >
                  <option value="">{t("clients.merge.choose")}</option>
                  {filteredClients
                    .filter((client) => String(client.id) !== keepId)
                    .map((client) => (
                      <option key={`absorb-${client.id}`} value={client.id}>
                        {client.name} — {client.phone_number}
                      </option>
                    ))}
                </Input>
              </div>
            </div>

            {suggestedAbsorb.length > 0 ? (
              <Alert color="light" className="border mb-3">
                <strong className="d-block mb-2">{t("clients.merge.suggestions_for_keep")}</strong>
                <div className="d-flex flex-wrap gap-2">
                  {suggestedAbsorb.map(({ client, reason }) => (
                    <Button
                      key={`suggest-${client.id}`}
                      color={absorbId === String(client.id) ? "primary" : "soft-secondary"}
                      size="sm"
                      onClick={() => {
                        setAbsorbId(String(client.id));
                        setPreview(null);
                      }}
                    >
                      {client.name}
                      <small className="ms-1 opacity-75">
                        ({t(`clients.merge.suggestion_reason.${reason}`)})
                      </small>
                    </Button>
                  ))}
                </div>
              </Alert>
            ) : null}

            <Button
              color="soft-primary"
              size="sm"
              onClick={handlePreview}
              disabled={loadingPreview || !keepId || !absorbId}
            >
              {loadingPreview ? <Spinner size="sm" className="me-1" /> : null}
              {t("clients.merge.preview_button")}
            </Button>

            {preview ? (
              <div className="mt-3">
                {!preview.can_merge ? (
                  <Alert color="warning">
                    {preview.blockers?.[0]?.message ?? t("clients.merge.blocked")}
                  </Alert>
                ) : (
                  <Alert color="info">{t("clients.merge.preview_ready")}</Alert>
                )}

                <h6 className="mb-2">{t("clients.merge.relations_title")}</h6>
                <ul className="mb-3">
                  {Object.entries(preview.relations_to_move).map(([key, count]) => (
                    <li key={key}>
                      {t(`clients.merge.relation.${key}`, { defaultValue: key })}: {count}
                    </li>
                  ))}
                </ul>

                {(preview.tags_union?.length ?? 0) > 0 ? (
                  <p className="mb-3">
                    <strong>{t("clients.merge.tags_union")}:</strong>{" "}
                    {preview.tags_union.join(", ")}
                  </p>
                ) : null}

                <h6 className="mb-2">{t("clients.merge.fields_title")}</h6>
                <div className="table-responsive">
                  <Table size="sm" bordered className="mb-0">
                    <thead>
                      <tr>
                        <th>{t("clients.merge.field")}</th>
                        <th>{t("clients.merge.result")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["name", "phone", "email", "document_number", "notes"].map((field) => (
                        <tr key={field}>
                          <td>{t(`clients.merge.field_name.${field}`, { defaultValue: field })}</td>
                          <td>
                            {preview.field_plan?.[field]?.result == null ||
                            preview.field_plan?.[field]?.result === ""
                              ? "—"
                              : String(preview.field_plan[field].result)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            ) : null}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={confirming}>
          {t("clients.actions.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={!preview?.can_merge || confirming || tooFewClients}
        >
          {confirming ? <Spinner size="sm" className="me-1" /> : null}
          {t("clients.merge.confirm")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientMergeModal;
